import { ref } from "vue";
import type { CellCoord } from "./types";

export function useGridState() {
  const activeCell = ref<CellCoord | null>(null);
  const editingCell = ref<CellCoord | null>(null);
  const editValue = ref<any>(null);
  const initialChar = ref<string | null>(null);
  const selectedRowIds = ref<Set<string | number>>(new Set());
  const expandedIds = ref<Set<string | number>>(new Set());

  const isActive = (row: number, col: number) =>
    activeCell.value?.rowIndex === row && activeCell.value?.colIndex === col;

  const isEditing = (row: number, col: number) =>
    editingCell.value?.rowIndex === row && editingCell.value?.colIndex === col;

  const setActive = (row: number, col: number) => {
    activeCell.value = { rowIndex: row, colIndex: col };
    editingCell.value = null;
    editValue.value = null;
    initialChar.value = null;
  };

  const startEditing = (row: number, col: number, value: any, char?: string) => {
    activeCell.value = { rowIndex: row, colIndex: col };
    editingCell.value = { rowIndex: row, colIndex: col };
    editValue.value = value;
    initialChar.value = char ?? null;
  };

  const stopEditing = () => {
    editingCell.value = null;
    editValue.value = null;
    initialChar.value = null;
  };

  const clearActive = () => {
    activeCell.value = null;
    editingCell.value = null;
    editValue.value = null;
    initialChar.value = null;
  };

  const toggleRowSelection = (id: string | number) => {
    const next = new Set(selectedRowIds.value);
    next.has(id) ? next.delete(id) : next.add(id);
    selectedRowIds.value = next;
  };

  const setAllSelected = (allIds: Array<string | number>) => {
    selectedRowIds.value =
      selectedRowIds.value.size === allIds.length ? new Set() : new Set(allIds);
  };

  const clearSelection = () => {
    selectedRowIds.value = new Set();
  };

  const toggleExpanded = (id: string | number) => {
    const next = new Set(expandedIds.value);
    next.has(id) ? next.delete(id) : next.add(id);
    expandedIds.value = next;
  };

  return {
    activeCell,
    editingCell,
    editValue,
    initialChar,
    selectedRowIds,
    expandedIds,
    isActive,
    isEditing,
    setActive,
    startEditing,
    stopEditing,
    clearActive,
    toggleRowSelection,
    setAllSelected,
    clearSelection,
    toggleExpanded,
  };
}
